import type { Role } from '@shared/session';

import { type ApiError, normalizeError } from './api-error';
import { rawClient } from './raw-client';
import {
  type RefreshSessionRequest,
  type RefreshSessionResponse,
  sessionEndpoints,
} from './session.endpoints';
import { applySession } from './session-apply';

/**
 * 토큰 갱신 결과 3분류
 * - ok          : 새 토큰을 받았다
 * - rejected    : 서버가 거절했다(401 · 403). 세션이 끝났다
 * - unreachable : 서버에 닿지 못했다(네트워크 오류 · 타임아웃 · 5xx · 그 밖). **세션을 유지한다**
 */
export type RefreshOutcome =
  | { result: 'ok' }
  | { result: 'rejected'; error: ApiError }
  | { result: 'unreachable'; error: ApiError };

let inFlight: Promise<RefreshOutcome> | null = null;

async function requestRefresh(targetRole?: Role): Promise<RefreshOutcome> {
  try {
    const body: RefreshSessionRequest = targetRole ? { targetRole } : {};
    const { data } = await rawClient.request<RefreshSessionResponse>({
      method: sessionEndpoints.refreshSession.method,
      url: sessionEndpoints.refreshSession.path,
      data: body,
    });
    applySession(data);
    return { result: 'ok' };
  } catch (caught) {
    const error = normalizeError(caught);
    const rejected = error.status === 401 || error.status === 403;
    return { result: rejected ? 'rejected' : 'unreachable', error };
  }
}

/**
 * single-flight: 진행 중인 갱신이 있으면 그 Promise 를 함께 기다린다.
 * 동시에 401 을 받은 요청이 여러 개여도 갱신 요청은 한 번만 나간다.
 * 역할 전환(`targetRole`)은 진행 중인 갱신이 끝난 뒤 따로 보낸다.
 */
export function refreshSession(options: { targetRole?: Role } = {}): Promise<RefreshOutcome> {
  if (options.targetRole) {
    const previous = inFlight ?? Promise.resolve();
    const switching = previous.then(() => requestRefresh(options.targetRole));
    inFlight = switching;
    void switching.finally(() => {
      if (inFlight === switching) inFlight = null;
    });
    return switching;
  }

  if (inFlight) return inFlight;
  const current = requestRefresh();
  inFlight = current;
  void current.finally(() => {
    if (inFlight === current) inFlight = null;
  });
  return current;
}
